import { Box, Divider, Grid, Paper, Typography } from '@mui/material';
import { AppDispatch, RootStateType } from '@Store';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SBDropdown from '@Components/Dropdown';
import { useFormik } from 'formik';
import SbButton from '@Components/Button';
import { DutyTypeOptions, onForward, PaymentTypeOptions } from './constants';
import * as Yup from 'yup';
import Loader from '@Components/Loader';
import { downloadCarrierLabel } from '@Reducers/ShipmentHistory/actions';
import { DeclareCurrencyTypeOptions } from '../../../ViewSingleShipmentAdmin/components/ShipmentProcessingV2/components/Linex/constants';
import SbTextField from '@Components/Textfield';

// ------- VALIDATION SCHEMA -------
const iThinkLogisticsInformationSchema = Yup.object().shape({
  type: Yup.string().required('Type is required'),
  accountNo: Yup.mixed()
    .test('is-valid', 'Account is required', (value: any) => {
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'object') return true;
      return !!value;
    })
    .required('Account is required'),
  dutyType: Yup.string()
    .transform(value => (Array.isArray(value) ? '' : value))
    .required('Duty type is required'),
  PaymentType: Yup.string()
    .transform(value => (Array.isArray(value) ? '' : value))
    .required('Payment Type is required'),
  declareCurrency: Yup.string()
    .transform(value => (Array.isArray(value) ? '' : value))
    .required('Declare Currency is required'),
});

interface propsTypes {
  onClickForward: () => void;
  onClose?: () => void;
  initialStates: any;
  isEditable?: boolean;
  showFedExDetails: boolean;
  btnText: string;
}

const UserLinex = (props: propsTypes) => {
  const { onClickForward, onClose, initialStates, isEditable, showFedExDetails, btnText } = props;
  const dispatch = useDispatch<AppDispatch>();
  const [loading, setLoading] = useState<boolean>(false);
  const invoiceNo = useSelector((state: RootStateType) => state.shipment.shipmentById?._packageId.invoiceNumber);
  const { shipmentById, userLinexDetails: userLinexSettings } = useSelector((state: RootStateType) => state.shipment);
  const { linexDetails } = useSelector((state: RootStateType) => state.adminShipment);

  // Country options
  const countryOptions = [
    { value: 'UK', display: 'UK' },
    { value: 'EUROPE', display: 'Europe' },
  ];

  // Service options based on country
  const getServiceOptions = (country: string) => {
    if (country === 'UK') {
      return [{ value: 'HOME', display: 'HOME' }];
    } else if (country === 'EUROPE') {
      return [
        { value: 'HOME', display: 'HOME' },
        { value: 'HOME_YCC', display: 'HOME_YCC' },
      ];
    }
    return [];
  };

  // Custom validation based on new fields
  const validateForm = (values: any) => {
    const errors: any = {};

    // Existing validations from schema
    if (!values.accountNo) errors.accountNo = 'Required';
    if (!values.dutyType) errors.dutyType = 'Required';
    if (!values.PaymentType) errors.PaymentType = 'Required';
    if (!values.declareCurrency) errors.declareCurrency = 'Required';

    // New validations
    if (!values.country) errors.country = 'Required';
    if (!values.serviceCode) errors.serviceCode = 'Required';

    // IOSS number validation for Europe -> HOME
    if (values.country === 'EUROPE' && values.serviceCode === 'HOME' && !values.ShipperVATNumber) {
      errors.ShipperVATNumber = 'IOSS Number is required for Europe HOME service';
    }

    return errors;
  };

  // ------------ FORMIK ------------
  const { values, setFieldValue, errors, handleSubmit, handleChange } = useFormik<any>({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: {
      dutyType: '',
      PaymentType: '',
      declareCurrency: '',
      country: 'UK',
      serviceCode: '',
      ShipperVATNumber: '',
      ...initialStates,
      invoiceNumber: invoiceNo,
      accountNo: userLinexSettings?.accountNo?.length
        ? userLinexSettings?.accountNo.filter((acc: any) => acc.value.startsWith(initialStates.accountNo))
        : [],
    },
    validationSchema: iThinkLogisticsInformationSchema,
    validate: validateForm,
    onSubmit(values) {
      // Prepare data for backend
      const submitValues = {
        ...values,
        // If HOME_YCC is selected for Europe or UK is selected, send empty string for ShipperVATNumber
        ShipperVATNumber:
          (values.country === 'EUROPE' && values.serviceCode === 'HOME_YCC') || values.country === 'UK'
            ? ''
            : values.ShipperVATNumber,
      };

      onForward({
        values: submitValues,
        shipmentNumber: shipmentById?.shipmentNumber || '',
        dispatch,
        nextFunc: onClickForward,
        setLoading,
      });
    },
  });

  // Get service options based on selected country
  const serviceOptions = getServiceOptions(values.country);

  // Effect to handle service when country changes - ONLY when country changes from user interaction
  useEffect(() => {
    // Skip if we have initialStates with values (editing mode)
    if (initialStates?.country || initialStates?.serviceCode) {
      return;
    }

    // Only run for new entries (no initialStates)
    if (values.country === 'UK') {
      setFieldValue('serviceCode', 'HOME');
      setFieldValue('ShipperVATNumber', '');
    }
    // Reset service when switching to Europe
    if (values.country === 'EUROPE' && values.serviceCode === 'HOME') {
      // Keep HOME if it was already selected
    } else if (values.country === 'EUROPE') {
      setFieldValue('serviceCode', '');
    }
  }, [values.country, initialStates]);

  // Effect to handle ShipperVATNumber when service changes - ONLY when service changes from user interaction
  useEffect(() => {
    // Skip if we have initialStates with values (editing mode)
    if (initialStates?.country || initialStates?.serviceCode) {
      return;
    }

    // Only run for new entries (no initialStates)
    if (values.country === 'EUROPE' && values.serviceCode === 'HOME_YCC') {
      setFieldValue('ShipperVATNumber', '');
    }
    if (values.country === 'UK') {
      setFieldValue('ShipperVATNumber', '');
    }
  }, [values.serviceCode, values.country, initialStates]);

  // Handle ShipperVATNumber from initialStates (note the capital 'S')
  useEffect(() => {
    if (initialStates?.ShipperVATNumber !== undefined) {
      setFieldValue('ShipperVATNumber', initialStates.ShipperVATNumber);
    }
  }, [initialStates]);

  return (
    <>
      <Loader overly loading={loading}></Loader>
      {showFedExDetails && (
        <Paper
          variant="outlined"
          sx={{
            my: '2rem',
            p: '2rem',
          }}
        >
          <Grid container item xs={12}>
            <Grid container item mb={3}>
              <Grid item xs={12} md={6}>
                <Typography className="font-medium l-text pb-3">Linex Information</Typography>
              </Grid>
            </Grid>
            <Grid container item xs={12} md={12} lg={12} columnSpacing={4} rowSpacing={4}>
              {/* Country Selection */}
              <Grid item xs={12} md={6}>
                <SBDropdown
                  disabled={!isEditable}
                  id="country"
                  label="Country"
                  required
                  placeholder="Select Country"
                  name="country"
                  value={values.country ? [values.country] : []}
                  options={countryOptions}
                  onChange={value => {
                    setFieldValue('country', value[0]);
                  }}
                  error={errors.country as string}
                />
              </Grid>

              {/* Service Dropdown */}
              <Grid item xs={12} md={6}>
                <SBDropdown
                  disabled={!isEditable || !values.country}
                  id="serviceCode"
                  label="Service"
                  required
                  placeholder="Select Service"
                  name="serviceCode"
                  value={values.serviceCode ? [values.serviceCode] : []}
                  options={serviceOptions}
                  onChange={value => {
                    setFieldValue('serviceCode', value[0]);
                  }}
                  error={errors.serviceCode as string}
                />
              </Grid>

              {/* IOSS Number (ShipperVATNumber) */}
              {values.country === 'EUROPE' && values.serviceCode === 'HOME' && (
                <Grid item xs={12} md={6}>
                  <SbTextField
                    disabled={!isEditable}
                    label={`IOSS Number`}
                    required
                    value={values.ShipperVATNumber}
                    placeholder="Enter IOSS Number"
                    onChange={handleChange}
                    name="ShipperVATNumber"
                    error={errors.ShipperVATNumber as string}
                  />
                </Grid>
              )}

              <Grid container item xs={12} md={12} lg={12} columnSpacing={4} rowSpacing={4}>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="accountNo"
                    label="Linex Account"
                    required
                    placeholder="Select"
                    name="accountNo"
                    value={
                      Array.isArray(values?.accountNo)
                        ? values.accountNo.map((item: { value: any }) => item.value)
                        : values.accountNo
                        ? [values.accountNo]
                        : []
                    }
                    options={userLinexSettings?.accountNo ?? []}
                    onChange={(_, selectedObj) => {
                      setFieldValue('accountNo', selectedObj);
                    }}
                    error={errors.accountNo as string}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <SBDropdown
                      disabled={!isEditable}
                      id="dutyType"
                      label="Duty Type"
                      required
                      placeholder="Select"
                      name="dutyType"
                      value={values.dutyType ? [values.dutyType] : []}
                      options={userLinexSettings?.dutyType?.length ? userLinexSettings.dutyType : DutyTypeOptions}
                      onChange={val => {
                        const selected = val[0] || '';
                        setFieldValue('dutyType', selected);
                      }}
                      error={errors.dutyType as string}
                    />

                    <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, ml: 1.5, display: 'block' }}>
                      Note: If shipment amount is less than 150 euro then choose DDP option
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              <Grid container item xs={12} md={12} lg={12} columnSpacing={4} rowSpacing={4}>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="PaymentType"
                    label="Payment Type"
                    required
                    placeholder="Select"
                    name="PaymentType"
                    value={values.PaymentType ? [values.PaymentType] : []}
                    options={
                      userLinexSettings?.PaymentType?.length ? userLinexSettings.PaymentType : PaymentTypeOptions
                    }
                    onChange={val => {
                      const selected = val[0] || '';
                      setFieldValue('PaymentType', selected);
                    }}
                    error={errors.PaymentType as string}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="declareCurrency"
                    label="Declare Currency"
                    required
                    placeholder="Select"
                    name="declareCurrency"
                    value={values.declareCurrency ? [values.declareCurrency] : []}
                    options={
                      userLinexSettings?.declareCurrency?.length
                        ? userLinexSettings.declareCurrency
                        : DeclareCurrencyTypeOptions
                    }
                    onChange={val => {
                      const selected = val[0] || '';
                      setFieldValue('declareCurrency', selected);
                    }}
                    error={errors.declareCurrency as string}
                  />
                </Grid>
              </Grid>

              <Divider className="mt-3 mb-3" />
              <Grid container item xs={12} md={12} lg={12} columnSpacing={4} rowSpacing={4}>
                <Grid container item sx={{ gap: '1rem' }}>
                  <Grid item xs={12} sm={5} md={2.6} lg={2.7}>
                    <SbButton
                      disabled={!isEditable}
                      variant="contained"
                      sx={{ borderRadius: '4px !important' }}
                      className="w-full"
                      onClick={_event => {
                        handleSubmit();
                      }}
                    >
                      {btnText}
                    </SbButton>
                  </Grid>
                  {onClose && (
                    <Grid item xs={12} sm={5} md={2.6} lg={2.7}>
                      <SbButton
                        sx={{ borderRadius: '4px !important' }}
                        className="w-full"
                        variant="outlined"
                        onClick={onClose}
                      >
                        Cancel
                      </SbButton>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      )}
      {linexDetails?.response && (
        <Grid container item>
          <Grid item xs={'auto'} border={'1px solid'} className="border-grey font-medium md-text p-2" borderRadius={1}>
            This shipment has been forwarded.
            <Box
              component={'span'}
              className="font-medium md-text ml-1"
              sx={{
                color: '#485fc7',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
              onClick={() => {
                if (shipmentById?.shipmentNumber) {
                  dispatch(downloadCarrierLabel(shipmentById?.shipmentNumber));
                }
              }}
            >
              Download the carrier AWB
            </Box>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default UserLinex;
